import { FormattedMessage, useIntl } from "react-intl";
import {
  DateRange,
  MultiInputTimeRangeField,
  MultiInputTimeRangeFieldProps,
} from "@mui/x-date-pickers-pro";
import { DateTime } from "luxon";

import { TooltipLabel } from "../../Tooltips";
import { datePickerVariantSx, DatePickerVariant } from "./styles";

type CustomTimeRangePickerProps = {
  onChange: (value: DateRange<DateTime<boolean>>, type: "time") => void;
  value: DateRange<DateTime<boolean>> | undefined;
  variant?: DatePickerVariant;
} & Omit<MultiInputTimeRangeFieldProps, "onChange" | "renderInput" | "value">;

/**
 * Time range picker — standalone, no Formik.
 * Use when a start time + end time (HH:mm) is needed without a date component.
 *
 * - `value` / `onChange`: Luxon DateRange<DateTime> (time portion only)
 * - `variant`: "filled" (default, no border) or "outlined" (with border, no background)
 * - Rendered via MultiInputTimeRangeField (two side-by-side fields)
 * - "TO" separator between the two fields
 */
const CustomTimeRangePicker = (props: CustomTimeRangePickerProps) => {
  const { onChange, value, variant = "filled" } = props;
  const intl = useIntl();

  return (
    <MultiInputTimeRangeField
      onChange={(newValue) => onChange(newValue, "time")}
      slotProps={{
        separator: { children: <FormattedMessage id={"TO"} /> },
        textField: ({ position }) => ({
          sx: [datePickerVariantSx[variant], { minWidth: 0 }],
          label:
            position === "start" ? (
              intl.formatMessage({ id: "TIME_START" })
            ) : (
              <TooltipLabel
                label={intl.formatMessage({ id: "TIME_END" })}
                tooltipLabel={"TIME_INCLUDED"}
              />
            ),
          variant: "outlined",
        }),
      }}
      sx={{ mt: 2.25, mb: 2, alignItems: "center", width: "100%" }}
      value={value}
    />
  );
};

export default CustomTimeRangePicker;
